// Rasterizes the live chart panels for the PDF. The bar/stacked charts are
// HTML divs (only the donut is SVG), so this goes through html-to-image's
// foreignObject serialization rather than raw SVG capture. Panels are located
// by the data-card-id charts-row already stamps for the Concierge highlight.
import { toPng } from 'html-to-image'

export type Rasterize = (node: HTMLElement) => Promise<string>

// Panel chrome filtered out of the shot: literal buttons, the segmented
// action-toggle wrapper (a role="group" pill — dropping only its inner
// <button>s would leave an empty bordered pill), and the drag-reorder grip.
// The role="group" check is scoped to elements that contain a <button>
// descendant rather than any role="group" element: the DS Gauge (the donut
// itself, real chart content) also sets role="group" on its wrapper when
// segment clicks are wired, and that wrapper only ever contains an svg — a
// blanket role="group" exclusion would silently drop the donut from PDFs.
export function captureFilter(el: Node): boolean {
	if (!(el instanceof HTMLElement)) return true
	if (el.tagName === 'BUTTON') return false
	if (el.getAttribute?.('role') === 'group' && el.querySelector('button')) return false
	const cls = typeof el.className === 'string' ? el.className : ''
	if (cls.includes('cg-grip')) return false
	return true
}

// 3x for crisp print.
const defaultRasterize: Rasterize = (node) =>
	toPng(node, {
		pixelRatio: 3,
		backgroundColor: '#ffffff',
		filter: captureFilter
	})

// Panels are shot at a wide fixed width so the full-width PDF chart blocks
// keep a page-friendly (landscape) aspect instead of the on-screen squarish
// one. The bar/stacked charts are fluid CSS layouts, so they reflow to the
// forced width; the live styles are restored after the shot. 900 (not wider)
// keeps the in-shot typography readable at print scale: the shot lands on a
// ~515pt page width, so glyphs print at 515/900 of their on-screen size.
const CAPTURE_WIDTH = 900

const settleLayout = () =>
	new Promise<void>((resolve) =>
		requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
	)

/** Capture each panel to a PNG data URI; a missing node or a failed capture
 *  yields null for that id (the PDF degrades that chart to a placeholder).
 *  While a panel is being shot it carries data-pdf-capture, which reveals the
 *  print-only elements (bar value labels, the Other footnote — hover-tooltip
 *  data that has no other representation on paper); always removed after, so
 *  the app never shows them. */
export async function captureCharts(
	ids: string[],
	rasterize: Rasterize = defaultRasterize
): Promise<Record<string, string | null>> {
	const out: Record<string, string | null> = {}
	// The PDF paper is always light. Chart shots serialize live computed styles,
	// so a dark-mode session would bake dark tokens into the report; force the
	// light theme for the duration of the shots and restore the live theme after.
	const html = document.documentElement
	const liveTheme = html.dataset.theme
	if (liveTheme === 'dark') {
		html.dataset.theme = 'light'
		await settleLayout()
	}
	try {
		await captureAll(ids, rasterize, out)
	} finally {
		if (liveTheme === 'dark') html.dataset.theme = liveTheme
	}
	return out
}

async function captureAll(
	ids: string[],
	rasterize: Rasterize,
	out: Record<string, string | null>
): Promise<void> {
	for (const id of ids) {
		const node = document.querySelector<HTMLElement>(`[data-card-id="${id}"]`)
		if (!node) {
			out[id] = null
			continue
		}
		node.setAttribute('data-pdf-capture', '')
		const prev = {
			width: node.style.width,
			maxWidth: node.style.maxWidth,
			flex: node.style.flex
		}
		node.style.width = `${CAPTURE_WIDTH}px`
		node.style.maxWidth = 'none'
		node.style.flex = 'none'
		await settleLayout()
		try {
			out[id] = await rasterize(node)
		} catch {
			out[id] = null
		} finally {
			node.removeAttribute('data-pdf-capture')
			node.style.width = prev.width
			node.style.maxWidth = prev.maxWidth
			node.style.flex = prev.flex
		}
	}
}
